import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Crown, Medal, Rocket, Loader2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/lib/seo";
import { Shell } from "@/pages/PublicVideo";
import { GoldRainRecords } from "@/components/GoldRainRecords";
import { QuestHeroRecords } from "@/components/quest/QuestHeroRecords";

const RING = ["from-amber-300 to-amber-500", "from-slate-200 to-slate-400", "from-orange-300 to-orange-500"];
const ICON = [Crown, Medal, Medal];
const ICON_COLOR = ["text-amber-500", "text-slate-400", "text-orange-500"];

const Photo = ({ u, size = "w-16 h-16", ring = "from-rose-300 to-fuchsia-400" }) => (
  <div className={`rounded-full p-[3px] bg-gradient-to-br ${ring} shrink-0`}>
    {u.picture
      ? <img src={u.picture} alt={u.name} loading="lazy" className={`${size} rounded-full object-cover bg-white`} />
      : <div className={`${size} rounded-full bg-white flex items-center justify-center font-display font-extrabold text-xl text-rose-500`}>{u.name?.[0]?.toUpperCase()}</div>}
  </div>
);

function MonthCard({ m, i }) {
  return (
    <li className="fade-up bg-white rounded-[2rem] border border-pink-100 shadow-sm overflow-hidden" style={{ animationDelay: `${i * 60}ms` }} data-testid={`hof-month-${m.month}`}>
      <div className="px-5 sm:px-7 py-4 bg-gradient-to-r from-rose-50 to-fuchsia-50 border-b border-pink-100 flex items-center justify-between">
        <h2 className="font-display text-xl font-extrabold">{m.label}</h2>
        <Trophy className="w-5 h-5 text-amber-500" />
      </div>
      <div className="p-5 sm:p-7 grid gap-6 md:grid-cols-[1fr_auto] md:items-center">
        {m.winners.length > 0 ? (
          <div className="grid grid-cols-3 gap-3 sm:gap-6">
            {m.winners.map((w) => {
              const Icon = ICON[w.place - 1];
              return (
                <div key={w.place} className={`flex flex-col items-center text-center gap-2 ${w.place === 1 ? "" : "pt-4"}`} data-testid={`hof-winner-${m.month}-${w.place}`}>
                  <div className="relative">
                    <Photo u={w} ring={RING[w.place - 1]} size={w.place === 1 ? "w-20 h-20 sm:w-24 sm:h-24" : "w-16 h-16 sm:w-20 sm:h-20"} />
                    <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-white shadow flex items-center justify-center"><Icon className={`w-4 h-4 ${ICON_COLOR[w.place - 1]}`} /></span>
                  </div>
                  <p className="mt-1 text-sm font-bold truncate max-w-full">{w.name}</p>
                  <p className="text-[11px] font-semibold text-slate-500">Platz {w.place} · {w.score} P</p>
                </div>
              );
            })}
          </div>
        ) : <p className="text-sm text-slate-400">Keine Punkte-Gewinner in diesem Monat.</p>}
        {m.climber && (
          <div className="flex md:flex-col items-center gap-3 md:gap-2 p-4 rounded-3xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-pink-500 text-white md:w-40 md:text-center" data-testid={`hof-climber-${m.month}`}>
            <Photo u={m.climber} ring="from-white to-pink-200" size="w-14 h-14" />
            <div className="min-w-0">
              <p className="text-[10px] font-extrabold uppercase tracking-widest text-white/80 inline-flex items-center gap-1"><Rocket className="w-3 h-3" /> Aufsteiger</p>
              <p className="font-bold truncate">{m.climber.name}</p>
              <p className="text-xs text-white/85">+{m.climber.levels} Level</p>
            </div>
          </div>
        )}
      </div>
    </li>
  );
}

export default function HallOfFame() {
  const { user } = useAuth();
  const [list, setList] = useState(null);
  usePageMeta({ title: "Ruhmeshalle", description: "Die Ruhmeshalle der itzPlay Community: alle Monats-Gewinner und Level-Aufsteiger mit Foto – sammle Punkte und werde selbst eine Legende." });
  useEffect(() => { api.get("/public/hall-of-fame").then(({ data }) => setList(data)).catch(() => setList([])); }, []);
  return (
    <Shell testId="hall-of-fame">
      <p className="text-xs tracking-[0.3em] uppercase font-extrabold text-rose-500">itzPlay</p>
      <h1 className="mt-2 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">Ruhmes<span className="text-rose-500">halle</span></h1>
      <p className="mt-3 text-slate-600 max-w-2xl">Hier verewigen wir jeden Monat die Top 3 Punktesammler und den größten Level-Aufsteiger unserer Community. 💖</p>
      <GoldRainRecords />
      <QuestHeroRecords />
      {!list ? <Loader2 className="mt-10 w-6 h-6 animate-spin text-rose-500" /> : list.length === 0 ? (
        <div className="mt-10 p-8 rounded-3xl bg-white border border-pink-100 text-center text-slate-500" data-testid="hof-empty">Die ersten Legenden werden am 1. des nächsten Monats gekürt – vielleicht bist du dabei!</div>
      ) : (
        <ol className="mt-10 space-y-6" data-testid="hof-list">{list.map((m, i) => <MonthCard key={m.month} m={m} i={i} />)}</ol>
      )}
      <div className="mt-10 rounded-3xl p-6 pink-gradient text-white shadow-xl shadow-rose-200 space-y-3" data-testid="hof-cta">
        <p className="font-display text-xl font-extrabold">Werde die nächste Legende!</p>
        <p className="text-sm text-pink-50">Sammle Punkte mit Beiträgen, Login, Spielen und Freunden – die Besten landen hier.</p>
        <Link to={user ? "/rangliste" : "/registrieren"} data-testid="hof-cta-btn" className="inline-flex items-center gap-2 px-6 h-12 rounded-full bg-white text-rose-600 font-extrabold text-sm hover:scale-[1.03] transition-transform">
          {user ? "Zur Rangliste" : "Kostenlos mitmachen"} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </Shell>
  );
}
